import { CommonModule, formatDate } from '@angular/common';
import { Component, Input, OnInit, inject } from '@angular/core';
import { ImpactStats, RiskSummary } from '../../../model/impact-analysis.model';
import { API_STATUS, ApiResponse, SearchConfig } from '../../../model/api.model';
import { CommonService } from '../../../services/common.service';
import { LoadingSkeletonComponent } from '../../../layout/loading-skeleton/loading-skeleton.component';

@Component({
  selector: 'app-impact-analysis-hero-summary',
  standalone: true,
  imports: [CommonModule, LoadingSkeletonComponent],
  templateUrl: './impact-analysis-hero-summary.component.html',
  styleUrl: './impact-analysis-hero-summary.component.scss'
})
export class ImpactAnalysisHeroSummaryComponent implements OnInit {
  private readonly commonService = inject(CommonService);

  @Input() stats!: ImpactStats;
  @Input() elementId!: any;
  @Input() groupType!: any;
  @Input() apiConfig?: SearchConfig;
  @Input() riskApiConfig?: SearchConfig;
  isLoading = true;
  isRiskLoading = true;

  formatRequiredByDate(value: string | undefined): string {
    if (!value) {
      return '-';
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? '-' : formatDate(parsed, 'MMMM d, y', 'en-US');
  }

  ngOnInit(): void {
    const apiConfig = this.apiConfig;
    if (apiConfig?.urlPath && apiConfig.path) {
      void this.loadStats(apiConfig, apiConfig.urlPath);
    } else {
      this.isLoading = false;
    }

    const riskApiConfig = this.riskApiConfig;
    if (riskApiConfig?.urlPath && riskApiConfig.path) {
      void this.loadRiskSummary(riskApiConfig, riskApiConfig.urlPath);
    } else {
      this.isRiskLoading = false;
    }
  }

  private async loadStats(apiConfig: SearchConfig, urlPath: string): Promise<void> {
    try {
      const response = await this.commonService.getDataFromDataSource(apiConfig, urlPath);
      if (response.status !== API_STATUS.Success) {
        return;
      }

      const source = this.extractSummary(response);
      if (!source || !this.stats) {
        return;
      }

      this.stats = {
        ...this.stats,
        losingMasterThisMonth: source.masterCertifiedCount ?? 0,
        losingSeniorThisMonth: source.seniorCertifiedCount ?? 0,
        losingLMLNextMonth: source.masterEnrolledPaymentCount ?? 0,
        certificationRequiredByDate: source.certificationRequiredByDate ,
        paymentRequiredByDate: source.paymentRequiredByDate ,
      };
    } catch (error) {
      console.info('Error fetching impact hero stats', error); 
    } finally {
      this.isLoading = false;
    }
  }

  private async loadRiskSummary(apiConfig: SearchConfig, urlPath: string): Promise<void> {
    try {
      const response = await this.commonService.getDataFromDataSource(apiConfig, urlPath);
      if (response.status !== API_STATUS.Success || !this.stats) {
        return;
      }

      const rows = this.extractRiskRows(response);
      this.stats = {
        ...this.stats,
        riskSummary: this.parseRiskSummary(rows),
      };
    } catch (error) {
      console.info('Error fetching impact risk summary', error);
    } finally {
      this.isRiskLoading = false;
    }
  }

  private extractRiskRows(response: ApiResponse): Record<string, unknown>[] {
    const responseData = response as ApiResponse & { payload?: unknown; result?: unknown };
    const source = responseData.payload ?? responseData.result;
    return Array.isArray(source) ? source as Record<string, unknown>[] : [];
  }

  private parseRiskSummary(rows: Record<string, unknown>[]): RiskSummary {
    const summary: RiskSummary = { losingLML: 0, high: 0, medium: 0, low: 0 };
    rows.forEach((row) => {
      const level = String(row['risk_level'] ?? row['riskLevel'] ?? '').trim().toLowerCase();
      const count = Number(row['associate_count'] ?? row['associateCount'] ?? 0) || 0;
      if (level === 'high') {
        summary.high = count;
      } else if (level === 'medium') {
        summary.medium = count;
      } else if (level === 'low') {
        summary.low = count;
      } else if (level === 'losing lml' || level === 'losinglml') {
        summary.losingLML = count;
      }
    });
    return summary;
  }

  private extractRecord(response: ApiResponse): Record<string, unknown> | undefined {
    const responseData = response as ApiResponse & { payload?: unknown; result?: unknown };
    const source = responseData.payload ?? responseData.result;
    const record = Array.isArray(source) ? source[0] : source;
    return record && typeof record === 'object' ? record as Record<string, unknown> : undefined;
  }

  private extractSummary(response: ApiResponse): {
    masterCertifiedCount?: number;
    seniorCertifiedCount?: number;
    masterEnrolledPaymentCount?: number;
    certificationRequiredByDate?: string;
    paymentRequiredByDate?: string;
  } | undefined {
    const responseData = response as ApiResponse & { payload?: unknown; result?: unknown };
    const payload = responseData.payload ?? responseData.result;
    if (!payload || typeof payload !== 'object') {
      return this.extractRecord(response);
    }

    const data = payload as Record<string, unknown>;
    const certification = Array.isArray(data['certification_Details'])
      ? data['certification_Details'][0] as Record<string, unknown> | undefined
      : undefined;
    const payment = Array.isArray(data['payment_Details'])
      ? data['payment_Details'][0] as Record<string, unknown> | undefined
      : undefined;

    if (!certification && !payment) {
      return this.extractRecord(response);
    }

    return {
      masterCertifiedCount: this.readOptionalNumber(certification, 'master_certified_count'),
      // seniorCertifiedCount: this.readOptionalNumber(certification, 'senior_certified_count'),
      masterEnrolledPaymentCount: this.readOptionalNumber(payment, 'master_enrolled_payment_count'),
      certificationRequiredByDate: this.readText(certification, 'required_by_date'),
      paymentRequiredByDate: this.readText(payment, 'required_by_date'),
    };
  }

  private readText(source: Record<string, unknown> | undefined, key: string): string | undefined {
    const value = source?.[key];
    return typeof value === 'string' ? value : undefined;
  }

  private readOptionalNumber(source: Record<string, unknown> | undefined, key: string): number | undefined {
    const value = Number(source?.[key]);
    return Number.isFinite(value) ? value : undefined;
  }

  private readNumber(source: Record<string, unknown>, keys: string[], fallback: number): number {
    const value = keys.map((key) => source[key]).find((candidate) => candidate !== undefined && candidate !== null);
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

}
